/**
 * Recently Played / history album-id resolution — local catalog hash vs Qobuz.
 *
 * Shared by home recents tiles, history cards, and tile play routing so the same
 * library-grid album id is used everywhere (#682).
 */
import { normalizeLocalAlbumId, isLocalEntityHash } from '../services/localLibraryMetadata';

export function resolveRecentAlbumIdFields(item) {
  return item?.album_key || item?.album_id || item?.albumId || item?.id || '';
}

export function resolveRecentLocalAlbumKey(item) {
  return normalizeLocalAlbumId(resolveRecentAlbumIdFields(item));
}

export function isRecentLocalHistoryItem(item) {
  if (!item) return false;
  if (item.source === 'local_library' || item.file_path) return true;
  const trackId = String(item.trackId || '');
  if (trackId.startsWith('local:')) return true;
  const albumId = String(item.albumId || '');
  if (albumId.startsWith('local:')) return true;
  return !!(albumId && isLocalEntityHash(albumId));
}

export function isLocalLibraryAlbum(album) {
  if (!album) return false;
  if (album.__source === 'local_library' || album.isLocalLibrary === true || album.source === 'local_library') {
    return true;
  }
  const id = String(resolveRecentAlbumIdFields(album));
  if (id.startsWith('qobuz:')) return false;
  if (id.startsWith('local:album:')) return true;
  if (isLocalEntityHash(id) && !album.serviceAlbumId) return true;
  return !!(album.album_key && !album.serviceAlbumId);
}

export function recentLocalSourceFlags(isLocal) {
  if (!isLocal) return {};
  return { source: 'local_library', __source: 'local_library', isLocalLibrary: true };
}

export function recentPlayCardFields(isLocal, albumId, source) {
  return {
    album_id: albumId,
    album_key: isLocal ? albumId : undefined,
    source: source || (isLocal ? 'local_library' : 'qobuz'),
    ...recentLocalSourceFlags(isLocal),
  };
}

export function recentLocalClickAlbum(recent, coverUrl) {
  return {
    album_key: recent.album_id,
    id: recent.album_id,
    title: recent.title,
    artistName: recent.artist || '',
    coverUrl,
    ...recentLocalSourceFlags(true),
  };
}

export function mapHistoryPageCard(item, localCover) {
  const isLocal = item.source === 'local_library' || !!item.file_path;
  const albumKey = isLocal ? resolveRecentLocalAlbumKey(item) : (item.albumId || null);
  return {
    ...item,
    id: albumKey || item.trackId || item.title,
    album_key: isLocal ? (albumKey || undefined) : undefined,
    albumId: albumKey || item.albumId || null,
    title: item.title || '',
    artistName: item.artist || '',
    coverUrl: localCover || item.artworkUrl || null,
    subtitle: item.albumTitle || '',
    ...recentLocalSourceFlags(isLocal),
    source: isLocal ? 'local_library' : (item.source || 'qobuz'),
  };
}

function stripQobuzAlbumId(id) {
  if (!id) return null;
  const bare = String(id).replace(/^qobuz:album:/i, '').trim();
  return bare || null;
}

/** Map a Supabase history row to an album object for nav.openAlbum / handleAlbumClick. */
export function historyItemToOpenAlbum(item) {
  if (!item) return null;

  const title = (item.albumTitle || '').trim();
  const artistName = (item.artist || '').trim();
  const coverUrl = item.artworkUrl || null;

  if (isRecentLocalHistoryItem(item)) {
    const albumKey = resolveRecentLocalAlbumKey(item);
    if (!albumKey || !isLocalEntityHash(albumKey)) return null;

    return {
      album_key: albumKey,
      id: albumKey,
      title: title || item.title || 'Unknown Album',
      artistName,
      artist_name: artistName,
      coverUrl,
      cover: coverUrl,
      source: 'local_library',
      __source: 'local_library',
      isLocalLibrary: true,
      machine_id: item.machine_id || null,
      embedded_cover_hash: item.embedded_cover_hash || null,
      folder_cover_hash: item.folder_cover_hash || null,
      _fromHistory: true,
    };
  }

  let qobuzAlbumId = stripQobuzAlbumId(item.albumId);
  if (!qobuzAlbumId && item.albumId) qobuzAlbumId = String(item.albumId).trim() || null;
  if (!qobuzAlbumId) return null;

  return {
    id: qobuzAlbumId,
    serviceAlbumId: qobuzAlbumId,
    title: title || item.title || 'Unknown Album',
    artistName,
    artist: artistName,
    coverUrl,
    cover: coverUrl,
    source: item.source || 'qobuz',
    _fromHistory: true,
  };
}
